'use client';

import { useState, useRef, useEffect, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { usePermissions } from '@/features/auth/hooks/use-permissions';
import { canOpenPath } from '@/shared/components/route-permissions';
import {
  Plus, FileText, Receipt, Truck, Users, Banknote, Package, ClipboardCheck,
} from 'lucide-react';

type QuickAction = {
  label: string;
  href: string;
  icon: typeof Plus;
  tone: 'amber' | 'sand' | 'graphite';
  shortcut?: string;
};

type ActionGroup = {
  label: string;
  items: QuickAction[];
};

const GROUPS: ActionGroup[] = [
  {
    label: 'Vânzări',
    items: [
      { label: 'Factură nouă', href: '/facturi/nou', icon: FileText, tone: 'amber', shortcut: 'F' },
      { label: 'Proformă nouă', href: '/proforme/nou', icon: FileText, tone: 'sand', shortcut: 'P' },
      { label: 'Aviz nou', href: '/avize/nou', icon: Truck, tone: 'sand', shortcut: 'A' },
    ],
  },
  {
    label: 'Achiziții',
    items: [
      { label: 'Cheltuială nouă', href: '/cheltuieli/nou', icon: Receipt, tone: 'graphite', shortcut: 'D' },
      { label: 'Recepție NIR nouă', href: '/receptie/nou', icon: ClipboardCheck, tone: 'graphite', shortcut: 'R' },
    ],
  },
  {
    label: 'Încasări',
    items: [
      { label: 'Chitanță nouă', href: '/chitante/nou', icon: Banknote, tone: 'graphite', shortcut: 'I' },
    ],
  },
  {
    label: 'Catalog',
    items: [
      { label: 'Client nou', href: '/clienti/nou', icon: Users, tone: 'graphite', shortcut: 'C' },
      { label: 'Produs nou', href: '/produse/nou', icon: Package, tone: 'graphite', shortcut: 'B' },
    ],
  },
];

const TONE_ICON: Record<QuickAction['tone'], string> = {
  amber: 'text-primary',
  sand: 'text-amber-700/80 dark:text-amber-500/80',
  graphite: 'text-neutral-500 dark:text-neutral-400',
};

/**
 * QuickCreateMenu — topbar dropdown for "Creează nou" actions. Replaces the
 * floating bottom-right FAB which conflicted with form action bars on
 * create/edit pages. Lives in the global topbar with its own reserved slot
 * so it never overlaps with page content.
 */
export function QuickCreateMenu() {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const router = useRouter();

  // Doar creările pe care utilizatorul le poate face (dreptul paginii de creare); un cont doar de citire nu vede meniul.
  const { can } = usePermissions();
  const groups = useMemo(
    () => GROUPS.map((g) => ({ ...g, items: g.items.filter((a) => canOpenPath(a.href, can)) })).filter((g) => g.items.length > 0),
    [can],
  );
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  useEffect(() => {
    if (!open) { setActiveIndex(0); return; }
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((i) => (i + 1) % flat.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((i) => (i - 1 + flat.length) % flat.length); return; }
      if (e.key === 'Enter') {
        e.preventDefault();
        const a = flat[activeIndex];
        if (a) { router.push(a.href); setOpen(false); }
        return;
      }
      // Single-letter shortcuts (no modifier)
      if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const k = e.key.toUpperCase();
        const match = flat.find((a) => a.shortcut === k);
        if (match) {
          e.preventDefault();
          router.push(match.href);
          setOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, flat, activeIndex, router]);

  let globalIdx = 0;

  if (flat.length === 0) return null;

  return (
    <div ref={ref} className="relative">
      {/* Trigger — same h-10 w-10 footprint as the other topbar action buttons
          (HelpMenu, NotificationBell) so the layout stays balanced. Primary
          color background pulls focus as the most-frequent CTA in the app. */}
      <button
        aria-label={open ? 'Închide meniul' : 'Creează nou'}
        aria-expanded={open}
        aria-haspopup="menu"
        title="Creează nou"
        onClick={() => setOpen((v) => !v)}
        className={
          'relative flex h-10 w-10 items-center justify-center rounded-lg transition-all duration-200 ' +
          'shadow-[0_4px_12px_-2px_rgb(var(--primary-500)/0.35),0_1px_3px_rgba(15,23,42,0.08)] ' +
          (open
            ? 'bg-neutral-900 dark:bg-white rotate-45 shadow-[0_2px_6px_-1px_rgba(15,23,42,0.2)]'
            : 'bg-primary hover:scale-[1.04] hover:shadow-[0_8px_18px_-4px_rgb(var(--primary-500)/0.5),0_2px_6px_rgba(15,23,42,0.12)]')
        }
      >
        <Plus
          className={'h-4 w-4 transition-transform duration-200 ' + (open ? 'text-white dark:text-neutral-900' : 'text-white')}
          strokeWidth={2.5}
        />
      </button>

      {/* Dropdown menu — opens below the trigger, anchored to right edge */}
      {open && (
        <div
          role="menu"
          aria-label="Creează rapid"
          className="absolute right-0 top-full mt-2 w-[296px] rounded-[18px] border border-neutral-200/90 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-[0_18px_40px_-8px_rgba(15,23,42,0.18),0_4px_12px_-2px_rgba(15,23,42,0.08)] dark:shadow-[0_24px_60px_-12px_rgba(0,0,0,0.6)] z-50 animate-in fade-in slide-in-from-top-2 duration-200 origin-top-right"
        >
          {/* Header strip */}
          <div className="px-5 pt-4 pb-3">
            <p className="text-[0.625rem] font-semibold tracking-[0.18em] uppercase text-neutral-500 dark:text-neutral-400">
              Creează nou
            </p>
            <div className="mt-2 h-px bg-gradient-to-r from-primary/60 via-primary/15 to-transparent" />
          </div>

          <div className="pb-2">
            {groups.map((group) => (
              <div key={group.label}>
                <div className="px-5 mt-1.5 mb-1.5 flex items-center gap-3">
                  <span className="text-[0.5625rem] font-semibold tracking-[0.16em] uppercase text-neutral-400 dark:text-neutral-500 shrink-0">
                    {group.label}
                  </span>
                  <span aria-hidden="true" className="flex-1 border-t border-dotted border-neutral-200 dark:border-neutral-700" />
                </div>

                {group.items.map((action) => {
                  const Icon = action.icon;
                  const idx = globalIdx++;
                  const isActive = idx === activeIndex;
                  return (
                    <button
                      key={action.href}
                      role="menuitem"
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={() => { router.push(action.href); setOpen(false); }}
                      style={{ animationDelay: `${idx * 28}ms` }}
                      className={
                        'group/item w-full flex items-center gap-3 px-5 py-2 text-sm transition-colors animate-in fade-in slide-in-from-right-1 duration-300 fill-mode-both ' +
                        (isActive
                          ? 'bg-primary/[0.06] dark:bg-primary/[0.10] text-neutral-900 dark:text-neutral-100'
                          : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800/60')
                      }
                    >
                      <Icon className={'h-4 w-4 shrink-0 transition-colors ' + TONE_ICON[action.tone]} strokeWidth={1.75} />
                      <span className="flex-1 text-left tracking-tight">{action.label}</span>
                      {action.shortcut && (
                        <kbd
                          className={
                            'shrink-0 inline-flex h-5 min-w-[20px] px-1.5 items-center justify-center rounded-md text-[0.625rem] font-mono font-medium transition-colors ' +
                            (isActive
                              ? 'border border-primary/30 text-primary bg-white dark:bg-neutral-900'
                              : 'border border-neutral-200 dark:border-neutral-700 text-neutral-400 dark:text-neutral-500 bg-neutral-50 dark:bg-neutral-800/40')
                          }
                        >
                          {action.shortcut}
                        </kbd>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Footer hint */}
          <div className="px-5 pt-2 pb-3 border-t border-dotted border-neutral-200 dark:border-neutral-800">
            <div className="flex items-center justify-between text-[0.625rem] text-neutral-400 dark:text-neutral-500">
              <div className="flex items-center gap-1.5">
                <kbd className="font-mono text-[0.5625rem] px-1 py-0.5 rounded border border-neutral-200 dark:border-neutral-700">↑</kbd>
                <kbd className="font-mono text-[0.5625rem] px-1 py-0.5 rounded border border-neutral-200 dark:border-neutral-700">↓</kbd>
                <span>navighează</span>
              </div>
              <div className="flex items-center gap-1.5">
                <kbd className="font-mono text-[0.5625rem] px-1 py-0.5 rounded border border-neutral-200 dark:border-neutral-700">Enter</kbd>
                <span>deschide</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
